import { transform } from 'sucrase'
import type { ComponentType } from 'react'
import * as React from 'react'
import * as Legion from '@foss/legion-ui'

export type CompileResult =
  | { ok: true; Component: ComponentType }
  | { ok: false; stage: 'transform' | 'evaluate'; message: string }

/**
 * Everything an example can reach. Legion's whole public surface plus the React
 * hooks, so a snippet can hold state without importing anything.
 *
 * Keys have to be valid identifiers because they become function parameters;
 * every legion export and React hook already is one.
 */
function buildScope(): Record<string, unknown> {
  const scope: Record<string, unknown> = {}
  for (const [key, value] of Object.entries(Legion)) {
    if (/^[A-Za-z_$][\w$]*$/.test(key)) scope[key] = value
  }
  for (const hook of [
    'useState',
    'useEffect',
    'useMemo',
    'useCallback',
    'useRef',
    'useId',
    'useReducer',
    'Fragment',
  ] as const) {
    scope[hook] = (React as unknown as Record<string, unknown>)[hook]
  }
  return scope
}

const SCOPE = buildScope()

/** The identifiers an example can use without importing them. */
export const SCOPE_KEYS = Object.keys(SCOPE).sort()

/**
 * A snippet that starts with JSX is an expression, and writing `return` in
 * front of it is noise. Anything else is a function body and must return.
 */
function toFunctionBody(source: string): string {
  const trimmed = source.trim()
  if (trimmed.startsWith('<')) return `return (\n${trimmed}\n)`
  return trimmed
}

/**
 * Compile an example into a component.
 *
 * The body becomes a real component function rather than a bare element, so a
 * snippet can call hooks — which is most of what makes an example worth
 * editing.
 *
 * `new Function` is not a sandbox: this evaluates whatever the reader typed,
 * with full access to the page. That is acceptable because the code is the
 * reader's own, typed into their own browser, and never persisted or shared
 * with anyone. If examples ever become shareable by URL, this has to be
 * revisited first — see docs/decisions.md.
 */
export function compile(source: string): CompileResult {
  if (!source.trim()) {
    // An empty body compiles to a component that returns undefined, which React
    // rejects with a confusing "Nothing was returned from render". Say what is
    // actually wrong instead.
    return {
      ok: false,
      stage: 'transform',
      message: 'The example is empty.',
    }
  }

  let code: string
  try {
    code = transform(`function Example() {\n${toFunctionBody(source)}\n}`, {
      transforms: ['jsx', 'typescript'],
      jsxRuntime: 'classic',
      production: true,
    }).code
  } catch (error) {
    return { ok: false, stage: 'transform', message: describe(error) }
  }

  try {
    const keys = Object.keys(SCOPE)
    const factory = new Function(
      'React',
      ...keys,
      `"use strict";\n${code}\nreturn Example;`
    )
    const Component = factory(React, ...keys.map((k) => SCOPE[k]))
    if (typeof Component !== 'function') {
      return {
        ok: false,
        stage: 'evaluate',
        message: 'The example did not produce a component.',
      }
    }
    return { ok: true, Component: Component as ComponentType }
  } catch (error) {
    return { ok: false, stage: 'evaluate', message: describe(error) }
  }
}

function describe(error: unknown): string {
  if (error instanceof Error) return error.message
  return String(error)
}
